import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormArray, FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatExpansionPanel } from '@angular/material/expansion';
import { MatIcon } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { DateTime } from 'luxon';
import { BehaviorSubject, map, shareReplay, startWith } from 'rxjs';

import { FilterFormComponent } from '../filter-form';
import { FiltersChipsComponent } from '../filters-chips';
import { Filter, FilterConfiguration, FilterForm, FilterType } from '../models/filters.model';
import { filterFormToFilterFactory, filterToFilterFormFactory } from '../utils/filters.utils';

@Component({
  selector: 'rms-filters',
  imports: [
    ReactiveFormsModule,
    AsyncPipe,
    TranslateModule,
    MatButton,
    MatIcon,
    FiltersChipsComponent,
    FilterFormComponent,
    MatExpansionPanel,
  ],
  templateUrl: './filters.component.html',
  styles: `
    :host {
      @apply flex flex-col gap-2 w-full;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FiltersComponent implements OnChanges {
  @Input({ required: true }) public config: FilterConfiguration[] = [];
  @Input({ required: true }) public showFilters = false;
  @Input() public specialLabel: string | null = null;
  @Input() public value: Filter[] | null = null;
  @Input() public preset: Filter[] | null = null;
  @Input() public min: DateTime | null = null;
  @Output() public applyFilter = new EventEmitter<Filter[]>();

  private readonly fb = inject(FormBuilder);

  protected form = this.fb.group({
    filters: this.fb.array<FilterForm[]>([]),
  });

  protected filters$ = new BehaviorSubject<Filter[]>([]);
  protected disabledFilters$ = this.form.controls.filters.valueChanges.pipe(
    startWith(this.form.controls.filters.value),
    map((filters) => {
      const disabledFilters: string[] = [];
      const uniqueFilters = this.config.filter((filter) => filter.unique).map(({ filter }) => filter);

      filters.forEach((filter) => {
        if (!filter || !filter.filter || !uniqueFilters.includes(filter.filter)) {
          return;
        }

        disabledFilters.push(filter.filter);
      });

      return disabledFilters;
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  protected hasAvailableFilter$ = this.disabledFilters$.pipe(
    map((disabledFilters) => disabledFilters.length !== this.config.length)
  );

  protected hasFiltersToApply$ = this.form.controls.filters.valueChanges.pipe(
    startWith(this.form.controls.filters.value),
    map((filters) => filters.some((f) => f?.filter && f?.operator))
  );

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value']) {
      const currentValue = changes['value'].currentValue;
      const previousValue = changes['value'].previousValue;

      if (JSON.stringify(currentValue) !== JSON.stringify(previousValue)) {
        if (currentValue && currentValue.length > 0) {
          this.applyExternalValue(currentValue);
        } else if (!changes['value'].firstChange) {
          this.filters.clear();
          this.filters$.next([]);
          this.add();
        } else {
          this.add();
        }
      }
    }

    if (changes['preset']) {
      const preset = changes['preset'].currentValue as Filter[] | null;

      if (preset) {
        this.applyFilters(preset);
      }
    }

    if (this.filters.length === 0) {
      this.add();
    }
  }

  protected get filters(): FormArray<FormControl<FilterForm | null>> {
    return this.form.controls.filters;
  }

  protected add(): void {
    this.filters.push(this.fb.control<FilterForm | null>(null));
  }

  protected remove(index: number): void {
    this.filters.removeAt(index);
  }

  protected removeChip(id: string): void {
    if (!id) {
      return;
    }

    const index = this.filters$.value.findIndex((filter) => filter.id === id);

    if (index === -1) {
      return;
    }

    this.filters$.next([...this.filters$.value.filter((filter) => filter.id !== id)]);

    this.remove(index);
    this.applyFilter.emit(this.filters$.value);
  }

  protected clear(): void {
    this.filters.clear();
    this.filters$.next([]);
    this.applyFilter.emit([]);

    setTimeout(() => {
      this.add();
    }, 0);
  }

  protected submit(): void {
    if (this.form.invalid) {
      return;
    }

    const filters = this.form.controls.filters.value.reduce((acc, form) => {
      const filter = filterFormToFilterFactory(this.config, form);
      if (filter) {
        const isDuplicate = acc.some(
          (existingFilter) =>
            existingFilter.filter === filter.filter &&
            existingFilter.operator === filter.operator &&
            JSON.stringify(existingFilter.type === FilterType.DATE ? existingFilter : existingFilter.value) ===
              JSON.stringify(filter.type === FilterType.DATE ? filter : filter.value)
        );

        if (!isDuplicate) {
          acc.push(filter);
        }
      }

      return acc;
    }, [] as Filter[]);

    this.filters$.next(filters);
    this.applyFilter.emit(filters);
  }

  private applyFilters(filters: Filter[]): void {
    const validFilters = filters
      .filter((f) => this.config.some((c) => c.filter === f.filter))
      .map((f) => {
        const configItem = this.config.find((c) => c.filter === f.filter);

        return {
          ...f,
          id: f.id ?? crypto.randomUUID(),
          label: f.label ?? configItem?.label,
        } as Filter;
      });

    this.filters.clear();

    if (validFilters.length === 0) {
      this.filters$.next([]);
      this.add();

      return;
    }

    validFilters.forEach((filter) => {
      this.filters.push(this.fb.control<FilterForm | null>(filterToFilterFormFactory(filter)));
    });

    this.filters$.next(validFilters);
    this.applyFilter.emit(validFilters);
    this.form.updateValueAndValidity();
  }

  private applyExternalValue(filters: Filter[]): void {
    this.applyFilters(filters);

    const uniqueFiltersUsed = filters.filter((f) => this.config.find((c) => c.filter === f.filter)?.unique).length;
    const totalUniqueFilters = this.config.filter((c) => c.unique).length;

    if (uniqueFiltersUsed < totalUniqueFilters || totalUniqueFilters < this.config.length) {
      this.add();
    }
  }
}
